import { newUuid } from '@/store/ids';

/**
 * The Section 12 identity HTTP surface.
 *
 * Only these calls are HTTP; everything else travels over the protobuf
 * envelope. Two constraints from the specification shape this module:
 *
 *   - The access token is held in memory and never written to browser storage.
 *     It is therefore a value the caller keeps and passes in, not something
 *     this module persists.
 *   - The refresh credential is a Secure HttpOnly cookie the page cannot read,
 *     so every call that needs it is made with `credentials: 'include'` and the
 *     rotated credential comes back the same way. Nothing here ever sees it.
 */

export interface IdentityError {
  code: string;
  message: string;
  retryable: boolean;
  retryAfterMs: number | null;
  requestId: string;
}

export class IdentityRequestError extends Error {
  readonly detail: IdentityError;
  readonly status: number;

  constructor(status: number, detail: IdentityError) {
    super(detail.message);
    this.name = 'IdentityRequestError';
    this.status = status;
    this.detail = detail;
  }
}

/** A failure that never reached the server, or reached it and gave no JSON. */
export class IdentityNetworkError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'IdentityNetworkError';
  }
}

export interface ChallengeResult {
  challengeId: string;
  expiresAt: string;
  resendAfterMs: number;
}

export interface VerifyResult {
  accountId: string;
  displayName: string;
  username: string | null;
  created: boolean;
  deviceId: string;
  sessionId: string;
  accessToken: string;
  accessExpiresAt: string;
}

export interface RefreshResult {
  accessToken: string;
  accessExpiresAt: string;
}

export interface TicketResult {
  ticket: string;
  expiresAt: string;
  endpoint: string;
}

export interface SessionSummary {
  id: string;
  deviceName: string;
  platform: string;
  createdAt: string;
  lastActiveAt: string;
  isCurrent: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function parseError(status: number, body: unknown): IdentityRequestError {
  const record = isRecord(body) ? body : {};
  return new IdentityRequestError(status, {
    code: asString(record['code'], 'INTERNAL'),
    // The server sends a bounded, safe message that deliberately never reveals
    // whether an account exists. It is shown to the user as-is rather than
    // being replaced by a guess about what went wrong.
    message: asString(record['message'], 'The request failed.'),
    retryable: record['retryable'] === true,
    retryAfterMs: typeof record['retry_after_ms'] === 'number' ? record['retry_after_ms'] : null,
    requestId: asString(record['request_id']),
  });
}

export class IdentityClient {
  private readonly origin: string;

  constructor(origin: string) {
    this.origin = origin.replace(/\/$/, '');
  }

  private async request(
    path: string,
    init: { method: string; body?: unknown; accessToken?: string },
  ): Promise<unknown> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      // A per-request identifier so a user-reported failure can be located in
      // the server log without the user reciting an error message.
      'X-Request-Id': newUuid(),
    };
    if (init.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (init.accessToken) {
      headers['Authorization'] = `Bearer ${init.accessToken}`;
    }

    let response: Response;
    try {
      response = await fetch(`${this.origin}${path}`, {
        method: init.method,
        headers,
        // The refresh cookie must travel on the calls that rotate it, and the
        // server validates Origin as its CSRF defence.
        credentials: 'include',
        cache: 'no-store',
        ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
      });
    } catch (error) {
      throw new IdentityNetworkError('could not reach the identity service', { cause: error });
    }

    const text = await response.text();
    let parsed: unknown = null;
    if (text.length > 0) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
    }

    if (!response.ok) {
      throw parseError(response.status, parsed);
    }
    return parsed;
  }

  async createChallenge(phoneE164: string, locale: string): Promise<ChallengeResult> {
    const body = await this.request('/v1/auth/challenges', {
      method: 'POST',
      body: { phone_e164: phoneE164, locale },
    });
    const record = isRecord(body) ? body : {};
    return {
      challengeId: asString(record['challenge_id']),
      expiresAt: asString(record['expires_at']),
      resendAfterMs: asNumber(record['resend_after_ms'], 60_000),
    };
  }

  async verify(input: {
    challengeId: string;
    code: string;
    deviceName: string;
    installationId: string;
  }): Promise<VerifyResult> {
    const body = await this.request('/v1/auth/verify', {
      method: 'POST',
      body: {
        challenge_id: input.challengeId,
        code: input.code,
        device_name: input.deviceName,
        // WEB is what makes the server set the refresh credential as a cookie
        // rather than returning it in a body this page could accidentally
        // persist.
        platform: 'WEB',
        installation_id: input.installationId,
      },
    });
    const record = isRecord(body) ? body : {};
    const account = isRecord(record['account']) ? record['account'] : {};
    return {
      accountId: asString(account['id']),
      displayName: asString(account['display_name']),
      username: typeof account['username'] === 'string' ? account['username'] : null,
      created: account['created'] === true,
      deviceId: asString(record['device_id']),
      sessionId: asString(record['session_id']),
      accessToken: asString(record['access_token']),
      accessExpiresAt: asString(record['access_expires_at']),
    };
  }

  /**
   * Rotates the refresh credential and mints a new access token.
   *
   * The request identifier is what makes a retry safe: replaying the same one
   * within sixty seconds returns the same successor, while reuse outside that
   * window revokes the whole token family. A caller that retries must therefore
   * reuse its identifier, which is why it is a parameter rather than generated
   * inside this method.
   */
  async refresh(refreshRequestId: string): Promise<RefreshResult> {
    const body = await this.request('/v1/auth/refresh', {
      method: 'POST',
      body: { refresh_request_id: refreshRequestId },
    });
    const record = isRecord(body) ? body : {};
    return {
      accessToken: asString(record['access_token']),
      accessExpiresAt: asString(record['access_expires_at']),
    };
  }

  async createConnectionTicket(accessToken: string): Promise<TicketResult> {
    const body = await this.request('/v1/connections/tickets', {
      method: 'POST',
      body: { transport: 'WSS' },
      accessToken,
    });
    const record = isRecord(body) ? body : {};
    return {
      ticket: asString(record['ticket']),
      expiresAt: asString(record['expires_at']),
      endpoint: asString(record['endpoint'], 'WSS'),
    };
  }

  async listSessions(accessToken: string): Promise<SessionSummary[]> {
    const body = await this.request('/v1/sessions', { method: 'GET', accessToken });
    const record = isRecord(body) ? body : {};
    const raw = Array.isArray(record['sessions']) ? record['sessions'] : [];
    return raw.filter(isRecord).map((entry) => ({
      id: asString(entry['id']),
      deviceName: asString(entry['device_name']),
      platform: asString(entry['platform']),
      createdAt: asString(entry['created_at']),
      lastActiveAt: asString(entry['last_active_at']),
      isCurrent: entry['is_current'] === true,
    }));
  }

  async logout(accessToken: string): Promise<void> {
    await this.request('/v1/auth/logout', { method: 'POST', body: {}, accessToken });
  }
}
